/**
 * AIQ (AI Quotient) 관련 훅
 *
 * AiqQuizStage 컴포넌트와 함께 사용됩니다.
 * 퀴즈 응답은 learningSessionApi를 통해 quiz_response_progress에 저장됩니다.
 */
import { useState, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { learningSessionApi } from '@/api/learningSession'
import type { QuizScript } from '@quebon/shared-learning'
import type { AiqSubmitRequest, AiqSubmitResponse } from '@/components/learner/AiqQuizStage'

// ============================================================================
// Types
// ============================================================================

export interface AiqProgress {
  totalQuestions: number
  completedQuestions: number
  correctAnswers: number
  progressPercentage: number
}

// Re-export types from AiqQuizStage
export type { AiqSubmitRequest, AiqSubmitResponse } from '@/components/learner/AiqQuizStage'

// ============================================================================
// API Functions
// ============================================================================

/**
 * Product AIQ 퀴즈 조회 API
 */
async function fetchProductAiqQuizzes(
  productId: number,
  count = 20,
  showAnswer = false,
  shuffle = false  // 기본값 false로 변경 (세션 재개 시 일관성 유지)
): Promise<QuizScript[]> {
  const response = await apiClient.get(`/quiz/product/${productId}/aiq`, {
    params: { count, showAnswer, shuffle },
  })
  return response.data.data
}

/**
 * 특정 퀴즈 ID 목록에 대한 스크립트 조회
 * 기존 GET /quiz/quizzes?ids=... 엔드포인트 활용
 */
async function fetchQuizScriptsByIds(
  quizIds: string[]
): Promise<QuizScript[]> {
  if (quizIds.length === 0) return []

  const response = await apiClient.get('/quiz/quizzes', {
    params: {
      ids: quizIds.join(','),
      showAnswer: false,
    },
  })
  return response.data.data
}

/**
 * 이전 응답 정보
 */
export interface AiqPreviousResponse {
  quizId: string
  isCorrect: boolean
  submittedResponse: (string | number | boolean)[]
  corrects: boolean[]
  correctAnswers: (string | number | boolean)[]
  difficultyLevel?: number  // 난이도 레벨 (1-5, 디버깅용)
}

/**
 * AIQ 세션 시작 API 응답
 */
interface AiqSessionStartResponse {
  sessionId: number
  isResume: boolean
  quizIds: string[]
  answeredCount: number
  totalCount: number
  previousResponses: AiqPreviousResponse[]
}

/**
 * AIQ 세션 시작 API
 */
async function startAiqSession(
  type: 'pre' | 'post',
  productId: number,
  classProductId?: number
): Promise<AiqSessionStartResponse> {
  const params = new URLSearchParams({ productId: String(productId) })
  if (classProductId) params.append('classProductId', String(classProductId))

  const endpoint = type === 'pre' ? '/aiq/pre/start' : '/aiq/post/start'
  const response = await apiClient.post(`${endpoint}?${params}`)
  return response.data.data
}

/**
 * AIQ 세션 완료 API
 */
async function completeAiqSession(
  type: 'pre' | 'post',
  sessionId: number
): Promise<{ message: string }> {
  const endpoint = type === 'pre' ? '/aiq/pre/complete' : '/aiq/post/complete'
  const response = await apiClient.post(endpoint, { sessionId })
  return response.data.data
}

// ============================================================================
// React Hooks
// ============================================================================

/**
 * AIQ 테스트 세션 훅
 *
 * Pre-AIQ 또는 Post-AIQ 테스트 전체 흐름을 관리합니다.
 * AiqQuizStage 컴포넌트와 함께 사용됩니다.
 */
export function useAiqSession(
  type: 'pre' | 'post',
  productId: number | undefined,
  classProductId: number | undefined
) {
  const queryClient = useQueryClient()
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [quizzes, setQuizzes] = useState<QuizScript[]>([])
  const [resumeFromIndex, setResumeFromIndex] = useState<number>(0)
  const [previousResponses, setPreviousResponses] = useState<AiqPreviousResponse[]>([])
  const [progress, setProgress] = useState<AiqProgress>({
    totalQuestions: 0,
    completedQuestions: 0,
    correctAnswers: 0,
    progressPercentage: 0,
  })

  // 세션 시작
  const startSession = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error('Product ID is required')

      // 1. 세션 시작 (기존 세션이 있으면 재사용)
      const session = await startAiqSession(type, productId, classProductId)

      // 2. 퀴즈 가져오기 (세션의 quizIds로 직접 조회)
      // 재개 시에는 세션에 저장된 정확한 퀴즈 ID로 스크립트 조회
      const fetchedQuizzes = await fetchQuizScriptsByIds(session.quizIds)

      // 3. 세션에 저장된 퀴즈 순서대로 정렬
      const orderedQuizzes = session.quizIds
        .map(id => fetchedQuizzes.find(q => q.quizId === id))
        .filter((q): q is QuizScript => q !== undefined)

      // 4. 모든 퀴즈를 찾지 못한 경우 fallback (새 세션일 때만 발생 가능)
      const finalQuizzes = orderedQuizzes.length === session.quizIds.length
        ? orderedQuizzes
        : fetchedQuizzes

      return {
        sessionId: session.sessionId,
        quizzes: finalQuizzes,
        answeredCount: session.answeredCount,
        isResume: session.isResume,
        previousResponses: session.previousResponses || [],
      }
    },
    onSuccess: (data) => {
      setSessionId(data.sessionId)
      setQuizzes(data.quizzes)
      setResumeFromIndex(data.answeredCount)
      setPreviousResponses(data.previousResponses)

      // 이전 응답에서 정답 수 계산
      const correctCount = data.previousResponses.filter(r => r.isCorrect).length

      setProgress({
        totalQuestions: data.quizzes.length,
        completedQuestions: data.answeredCount,
        correctAnswers: correctCount,
        progressPercentage: Math.round((data.answeredCount / data.quizzes.length) * 100),
      })
    },
  })

  // 세션 완료
  const completeSessionMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId) throw new Error('No active session')
      return completeAiqSession(type, sessionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiqStatus'] })
      queryClient.invalidateQueries({ queryKey: ['aiqStatusTransformed'] })
      queryClient.invalidateQueries({ queryKey: ['aiqStatuses'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'aiq'] })
    },
  })

  // AiqQuizStage에서 사용할 응답 제출 함수
  // learningSessionApi를 사용하여 quiz_response_progress에 저장
  const submitResponse = useCallback(
    async (quizId: string, request: AiqSubmitRequest): Promise<AiqSubmitResponse> => {
      if (!sessionId) throw new Error('No active session')

      // learningSessionApi.submitQuizResponse 사용 (DB에 저장됨)
      const response = await learningSessionApi.submitQuizResponse(sessionId, {
        quizId,
        scriptId: null,
        answers: request.answers,
        vars: request.vars ?? {},
        timeSpentSec: request.elapsedSec,
      })

      // QuizResponseResponse를 AiqSubmitResponse로 변환
      const result: AiqSubmitResponse = {
        correct: response.isCorrect,
        corrects: response.corrects,
        correctAnswers: response.correctAnswers as (string | number | boolean)[] | undefined,
        description: response.description ? {
          text: response.description.text ?? undefined,
          imageUrl: (response.description.image?.signedUrl ?? response.description.image?.url ?? response.description.image?.cloudfrontUrl) || undefined,
        } : undefined,
        difficultyLevel: response.difficultyLevel,
      }

      // 진행 상황 업데이트
      setProgress((prev) => {
        const newCompleted = prev.completedQuestions + 1
        const newCorrect = prev.correctAnswers + (result.correct ? 1 : 0)
        return {
          ...prev,
          completedQuestions: newCompleted,
          correctAnswers: newCorrect,
          progressPercentage: Math.round((newCompleted / prev.totalQuestions) * 100),
        }
      })

      return result
    },
    [sessionId]
  )

  // AiqQuizStage에서 사용할 세션 완료 함수
  const completeSessionFn = useCallback(async () => {
    await completeSessionMutation.mutateAsync()
  }, [completeSessionMutation])

  return {
    sessionId,
    quizzes,
    progress,
    resumeFromIndex,
    previousResponses,
    startSession,
    completeSession: completeSessionMutation,
    // AiqQuizStage용 함수들
    submitResponse,
    completeSessionFn,
    isStarting: startSession.isPending,
    isCompleting: completeSessionMutation.isPending,
    startError: startSession.error,
    completeError: completeSessionMutation.error,
  }
}

/**
 * AIQ 퀴즈 목록 조회 훅 (단독 사용)
 */
export function useAiqQuizzes(productId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['aiqQuizzes', productId],
    queryFn: () => fetchProductAiqQuizzes(productId!, 20, false),
    enabled: enabled && !!productId,
    staleTime: 5 * 60 * 1000, // 5분
  })
}
